import { useEffect, useState } from "react";
import { occurrenceApi, type OccurrenceDetailApi, type OccurrenceHistoryEntryApi, type OccurrenceSummaryApi } from "@/lib/api";
import {
  OCCURRENCE_ACTION_LABELS,
  OCCURRENCE_CLOSED_STATUSES,
  OCCURRENCE_PRIORITY_LABELS,
  OCCURRENCE_STATUS,
  OCCURRENCE_TYPE_LABELS,
} from "@/config/occurrence-config";

interface Props {
  occurrences: OccurrenceSummaryApi[];
  loading: boolean;
  error: string | null;
  /** Ausente quando o usuario nao tem permissao para registrar. */
  onRegister?: () => void;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function yesNo(v: boolean | null): string {
  return v === true ? "Sim" : v === false ? "Não" : "Não informado";
}

function OccurrenceDetails({ id }: { id: number }) {
  const [detail, setDetail] = useState<OccurrenceDetailApi | null>(null);
  const [history, setHistory] = useState<OccurrenceHistoryEntryApi[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([occurrenceApi.get(id), occurrenceApi.history(id)])
      .then(([d, h]) => {
        if (!alive) return;
        setDetail(d);
        setHistory(h);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : "Não foi possível carregar a ocorrência."));
    return () => {
      alive = false;
    };
  }, [id]);

  if (error) return <p className="text-xs text-red-700 px-4 pb-4">{error}</p>;
  if (!detail) return <p className="text-xs text-foreground-500 px-4 pb-4">Carregando…</p>;

  return (
    <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div className="space-y-2">
        <p className="text-foreground-700 whitespace-pre-line">{detail.description}</p>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <dt className="text-foreground-500">Obstrui via/calçada</dt><dd>{yesNo(detail.blocks_road)}</dd>
          <dt className="text-foreground-500">Rede elétrica</dt><dd>{yesNo(detail.hits_power_grid)}</dd>
          <dt className="text-foreground-500">Dano a imóvel/veículo</dt><dd>{yesNo(detail.has_property_damage)}</dd>
          <dt className="text-foreground-500">Pessoas feridas</dt><dd>{yesNo(detail.has_victims)}</dd>
          <dt className="text-foreground-500">Ação emergencial</dt>
          <dd>{detail.emergency_action_taken ? detail.emergency_action_description || "Sim" : yesNo(detail.emergency_action_taken)}</dd>
        </dl>
        {detail.cancel_reason && <p className="text-xs text-foreground-600">Motivo do cancelamento: {detail.cancel_reason}</p>}
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">Andamento</p>
        <ol className="space-y-2 border-l border-background-200 pl-3">
          {history.map((h) => (
            <li key={h.id} className="text-xs">
              <p className="font-medium text-foreground-800">
                {OCCURRENCE_ACTION_LABELS[h.action] ?? h.action} → {OCCURRENCE_STATUS[h.new_status]?.label ?? h.new_status}
              </p>
              <p className="text-foreground-500">
                {fmtDateTime(h.changed_at)}
                {h.changed_by_name ? ` · ${h.changed_by_name}` : ""}
              </p>
              {h.reason && <p className="text-foreground-600">{h.reason}</p>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function TreeOccurrences({ occurrences, loading, error, onRegister }: Props) {
  const [open, setOpen] = useState<number | null>(null);
  const openCount = occurrences.filter((o) => !OCCURRENCE_CLOSED_STATUSES.has(o.status)).length;

  return (
    <div className="bg-white rounded-card border border-background-200/70">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-background-200/70">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
            <i className="ri-alarm-warning-line text-warning-500" aria-hidden="true"></i>
            Ocorrências do exemplar
          </h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            {occurrences.length === 0 ? "Nenhuma ocorrência registrada." : `${occurrences.length} no total, ${openCount} em aberto.`}
          </p>
        </div>
        {onRegister && (
          <button type="button" onClick={onRegister} className="btn-primary text-xs">
            <i className="ri-add-line" aria-hidden="true"></i>
            Registrar ocorrência
          </button>
        )}
      </div>

      {loading && occurrences.length === 0 ? (
        <p className="p-4 text-sm text-foreground-500">Carregando ocorrências…</p>
      ) : error ? (
        <p className="p-4 text-sm text-red-700">{error}</p>
      ) : occurrences.length === 0 ? (
        <p className="p-6 text-sm text-foreground-500 text-center">
          Queda, galho caído, praga, dano ou supressão irregular deste exemplar são registrados aqui.
        </p>
      ) : (
        <ul className="divide-y divide-background-200/70">
          {occurrences.map((o) => {
            const st = OCCURRENCE_STATUS[o.status] ?? { label: o.status, badge: "bg-background-100 text-foreground-600" };
            const expanded = open === o.id;
            return (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : o.id)}
                  aria-expanded={expanded}
                  className="w-full text-left p-4 flex flex-wrap items-center gap-x-3 gap-y-1 hover:bg-background-50"
                >
                  <span className="font-mono text-xs text-foreground-600">{o.protocol}</span>
                  <span className="text-sm font-medium text-foreground-900">{OCCURRENCE_TYPE_LABELS[o.occurrence_type] ?? o.occurrence_type}</span>
                  <span className={`badge ${st.badge}`}>{st.label}</span>
                  <span className="text-xs text-foreground-500">
                    {o.priority ? OCCURRENCE_PRIORITY_LABELS[o.priority] ?? o.priority : `Sugerida ${o.suggested_priority}`}
                  </span>
                  <span className="text-xs text-foreground-500 sm:ml-auto">{fmtDateTime(o.reported_at)}</span>
                  <i className={`ri-arrow-${expanded ? "up" : "down"}-s-line text-foreground-400`} aria-hidden="true"></i>
                </button>
                {expanded && <OccurrenceDetails id={o.id} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
